import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Pause, Play, Trash2, Send, Key, X, Copy, AlertTriangle, Clock, CheckCircle2, XCircle } from "lucide-react";
import { webhooksApi, formatApiError } from "../../lib/ledgerApi";
import { toast } from "sonner";

const STATUS_COLOR = {
  active: "border-status-ok/40 bg-status-ok/10 text-status-ok",
  paused: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  disabled_by_system: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
};
const DELIVERY_COLOR = {
  succeeded: "border-status-ok/40 bg-status-ok/10 text-status-ok",
  pending: "border-status-info/40 bg-status-info/10 text-status-info",
  delivering: "border-status-info/40 bg-status-info/10 text-status-info",
  failed: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  exhausted: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
};

function Pill({ map, value, "data-testid": testid }) {
  return (
    <span data-testid={testid} className={`px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-widest ${map[value] || "border-border bg-muted/30 text-muted-foreground"}`}>
      {value?.replace(/_/g, " ") || "—"}
    </span>
  );
}

function DeliveryDrawer({ delivery, onClose, onRetry }) {
  const cmd = useMemo(() => {
    if (!delivery) return "";
    const sigHeader = delivery.request_signature || "";
    return `curl -X POST '${delivery.url}' \\
  -H 'Content-Type: application/json' \\
  -H 'X-NeoPay-Signature: ${sigHeader}' \\
  -H 'X-NeoPay-Delivery: ${delivery.id}' \\
  -H 'X-NeoPay-Event: ${delivery.event_type}' \\
  -H 'X-NeoPay-Event-Id: ${delivery.event_id}' \\
  -d '${JSON.stringify(delivery.payload).replace(/'/g, "'\\''")}'`;
  }, [delivery]);
  if (!delivery) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose} data-testid="delivery-drawer">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-card border-l border-border h-full overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Delivery</div>
            <div className="font-mono text-xs">{delivery.id}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" strokeWidth={1.5} /></button>
        </div>
        <div className="p-5 space-y-5 text-sm">
          <div className="flex items-center gap-3 flex-wrap">
            <Pill map={DELIVERY_COLOR} value={delivery.status} data-testid="drawer-status" />
            <span className="text-xs text-muted-foreground">Attempt #{delivery.attempt_number || 0}</span>
            {delivery.response_code && <span className="text-xs text-muted-foreground">HTTP {delivery.response_code}</span>}
            {delivery.response_time_ms != null && <span className="text-xs text-muted-foreground">{delivery.response_time_ms}ms</span>}
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div><div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">Event</div><div className="font-mono">{delivery.event_type}</div></div>
            <div><div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">Event id</div><div className="font-mono break-all">{delivery.event_id}</div></div>
            <div><div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">Scheduled</div><div className="font-mono">{delivery.scheduled_at ? new Date(delivery.scheduled_at).toLocaleString() : "—"}</div></div>
            <div><div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">Attempted</div><div className="font-mono">{delivery.attempted_at ? new Date(delivery.attempted_at).toLocaleString() : "—"}</div></div>
            {delivery.next_retry_at && (
              <div className="col-span-2"><div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">Next retry</div><div className="font-mono">{new Date(delivery.next_retry_at).toLocaleString()}</div></div>
            )}
          </div>

          <div>
            <div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">Signed payload</div>
            <pre className="bg-background border border-border rounded-sm p-3 text-[11px] font-mono overflow-x-auto" data-testid="drawer-payload">{JSON.stringify(delivery.payload, null, 2)}</pre>
          </div>

          {delivery.request_signature && (
            <div>
              <div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">Signature header</div>
              <div className="bg-background border border-border rounded-sm p-2 text-[11px] font-mono break-all">{delivery.request_signature}</div>
            </div>
          )}

          {delivery.response_body_truncated && (
            <div>
              <div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">Response body</div>
              <pre className="bg-background border border-border rounded-sm p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap break-all">{delivery.response_body_truncated}</pre>
            </div>
          )}

          {delivery.response_headers && Object.keys(delivery.response_headers).length > 0 && (
            <details>
              <summary className="text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer">Response headers</summary>
              <pre className="bg-background border border-border rounded-sm p-3 text-[11px] font-mono mt-2 overflow-x-auto">{JSON.stringify(delivery.response_headers, null, 2)}</pre>
            </details>
          )}

          <div>
            <div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">Reproduce as cURL</div>
            <pre className="bg-background border border-border rounded-sm p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap" data-testid="drawer-curl">{cmd}</pre>
            <button
              onClick={() => { navigator.clipboard.writeText(cmd); toast.success("cURL copied"); }}
              className="mt-2 text-xs border border-border px-3 py-1.5 rounded-sm hover:border-foreground inline-flex items-center gap-1.5"
              data-testid="drawer-copy-curl"
            >
              <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy
            </button>
          </div>

          <div className="flex gap-2 pt-2 border-t border-border">
            <button onClick={onRetry} className="text-xs border border-foreground bg-foreground text-background px-3 py-2 rounded-sm uppercase tracking-widest" data-testid="drawer-retry">
              Retry now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RotateModal({ open, sub, onClose, onComplete }) {
  const [secret, setSecret] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const doRotate = async () => {
    setBusy(true);
    try {
      const r = await webhooksApi.rotate(sub.id);
      setSecret(r.secret);
      toast.success("Rotated");
      onComplete?.();
    } catch (e) {
      toast.error("Rotate failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };
  const commit = async () => {
    try {
      await webhooksApi.commitRotation(sub.id);
      toast.success("Old secret retired");
      onComplete?.();
      onClose();
    } catch (e) {
      toast.error("Commit failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="rotate-modal">
      <div className="w-full max-w-lg bg-card border border-border rounded-sm">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2"><Key className="w-4 h-4" strokeWidth={1.5} /><span className="text-sm">Rotate signing secret</span></div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">×</button>
        </div>
        <div className="p-5 space-y-4">
          {!secret && !sub.rotation && (
            <>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Mints a new signing secret. Both old and new secrets will sign deliveries until you click <strong>Commit now</strong>.
                You'll have time to roll the new secret out across your services first.
              </p>
              <button onClick={doRotate} disabled={busy} className="text-xs border border-foreground bg-foreground text-background px-4 py-2 rounded-sm uppercase tracking-widest" data-testid="rotate-confirm">
                {busy ? "Rotating…" : "Rotate"}
              </button>
            </>
          )}
          {secret && (
            <>
              <div className="border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 p-3 rounded-sm flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
                <div className="text-xs">This is the only time you'll see the new secret. Both old + new sign deliveries until commit.</div>
              </div>
              <div className="border border-border rounded-sm p-3 font-mono text-xs break-all bg-background" data-testid="rotate-new-secret">{secret}</div>
              <button onClick={() => { navigator.clipboard.writeText(secret); toast.success("Copied"); }} className="text-xs border border-border px-3 py-1.5 rounded-sm hover:border-foreground">Copy</button>
            </>
          )}
          {sub.rotation && (
            <div className="border border-border rounded-sm p-3 text-xs space-y-1">
              <div><span className="text-muted-foreground">Rotation in progress.</span> Old secret valid until <span className="font-mono">{sub.rotation.expires_at ? new Date(sub.rotation.expires_at).toLocaleString() : "—"}</span></div>
              <div className="text-[11px] text-muted-foreground">Old prefix: <span className="font-mono">{sub.rotation.old_prefix}</span></div>
              <button onClick={commit} className="mt-2 text-xs border border-foreground bg-foreground text-background px-3 py-1.5 rounded-sm uppercase tracking-widest" data-testid="rotate-commit-now">
                Commit now (retire old)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function WebhookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [tab, setTab] = useState("overview");
  const [deliveries, setDeliveries] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [drawerDelivery, setDrawerDelivery] = useState(null);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const r = await webhooksApi.get(id);
      setSub(r.subscription);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  const loadDeliveries = async () => {
    try {
      const params = statusFilter ? { status: statusFilter, limit: 50 } : { limit: 50 };
      const r = await webhooksApi.deliveries(id, params);
      setDeliveries(r.items || []);
    } catch {/* ignore */}
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (tab === "deliveries") loadDeliveries(); }, [tab, statusFilter, id]);

  const sendTest = async () => {
    try {
      const r = await webhooksApi.test(id);
      const d = r.delivery;
      toast.success(`Test ${d.status === "succeeded" ? "succeeded" : "delivered (status " + d.status + ")"}`,
        { description: `HTTP ${d.response_code || "—"}, ${d.response_time_ms || 0}ms` });
      load();
      if (tab === "deliveries") loadDeliveries();
    } catch (e) {
      toast.error("Test failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const togglePause = async () => {
    try {
      await webhooksApi.update(id, { status: sub.status === "active" ? "paused" : "active" });
      load();
    } catch (e) {
      toast.error("Update failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this webhook?")) return;
    try {
      await webhooksApi.remove(id);
      toast.success("Deleted");
      navigate("/app/webhooks");
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const retryDelivery = async () => {
    if (!drawerDelivery) return;
    try {
      const r = await webhooksApi.retryDelivery(drawerDelivery.id);
      setDrawerDelivery(r.delivery);
      toast.success("Re-queued — refreshing");
      loadDeliveries();
    } catch (e) {
      toast.error("Retry failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  if (error) return <div className="p-10"><div className="border border-status-tampered/40 bg-status-tampered/10 p-4 rounded-sm text-sm text-status-tampered">{error}</div></div>;
  if (!sub) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-7xl px-6 md:px-10 py-10 md:py-14" data-testid="webhook-detail">
      <Link to="/app/webhooks" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back</Link>
      <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">/ Webhook</div>
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <h1 className="text-xl tracking-tight font-medium font-mono break-all" data-testid="detail-url">{sub.url}</h1>
        <Pill map={STATUS_COLOR} value={sub.status} data-testid="detail-status" />
      </div>
      {sub.description && <div className="text-sm text-muted-foreground mb-4">{sub.description}</div>}

      <div className="flex gap-2 flex-wrap mb-6">
        <button onClick={sendTest} className="text-xs border border-border px-3 py-2 rounded-sm hover:border-foreground inline-flex items-center gap-1.5" data-testid="detail-test"><Send className="w-3 h-3" strokeWidth={1.5} /> Send test</button>
        <button onClick={togglePause} className="text-xs border border-border px-3 py-2 rounded-sm hover:border-foreground inline-flex items-center gap-1.5" data-testid="detail-toggle">
          {sub.status === "active" ? <><Pause className="w-3 h-3" strokeWidth={1.5} /> Pause</> : <><Play className="w-3 h-3" strokeWidth={1.5} /> Resume</>}
        </button>
        <button onClick={() => setRotateOpen(true)} className="text-xs border border-border px-3 py-2 rounded-sm hover:border-foreground inline-flex items-center gap-1.5" data-testid="detail-rotate"><Key className="w-3 h-3" strokeWidth={1.5} /> Rotate secret</button>
        <button onClick={onDelete} className="text-xs border border-border px-3 py-2 rounded-sm hover:border-status-tampered text-muted-foreground hover:text-status-tampered inline-flex items-center gap-1.5" data-testid="detail-delete"><Trash2 className="w-3 h-3" strokeWidth={1.5} /> Delete</button>
      </div>

      {sub.consecutive_failure_count > 0 && (
        <div className="border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 p-3 rounded-sm text-xs mb-6 flex items-start gap-2" data-testid="detail-failure-warning">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>{sub.consecutive_failure_count} consecutive exhausted deliveries. Auto-disable triggers at 5.</div>
        </div>
      )}
      {sub.rotation && (
        <div className="border border-status-info/40 bg-status-info/10 text-status-info p-3 rounded-sm text-xs mb-6 flex items-center gap-2" data-testid="detail-rotation-banner">
          <Clock className="w-4 h-4" strokeWidth={1.5} />
          <span>Rotation in progress — old secret valid until <span className="font-mono">{new Date(sub.rotation.expires_at).toLocaleString()}</span>. Open Rotate secret modal to commit.</span>
        </div>
      )}

      <div className="border-b border-border flex gap-1 mb-6">
        {[["overview", "Overview"], ["deliveries", "Deliveries"], ["rotation", "Rotation"]].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${tab === k ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`detail-tab-${k}`}
          >{l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-border rounded-sm p-5 bg-card">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Configuration</div>
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-3 gap-2"><span className="text-muted-foreground text-xs">Created</span><span className="col-span-2 font-mono text-xs">{new Date(sub.created_at).toLocaleString()}</span></div>
              <div className="grid grid-cols-3 gap-2"><span className="text-muted-foreground text-xs">Last delivery</span><span className="col-span-2 font-mono text-xs">{sub.last_delivery_at ? new Date(sub.last_delivery_at).toLocaleString() : "—"}</span></div>
              <div className="grid grid-cols-3 gap-2"><span className="text-muted-foreground text-xs">Secret prefix</span><span className="col-span-2 font-mono text-xs">{sub.secret_prefix}…</span></div>
              <div className="grid grid-cols-3 gap-2"><span className="text-muted-foreground text-xs">Failures</span><span className="col-span-2 text-xs">{sub.consecutive_failure_count || 0} / 5</span></div>
            </div>
          </div>
          <div className="border border-border rounded-sm p-5 bg-card">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Subscribed events</div>
            <div className="flex flex-wrap gap-1.5">
              {sub.event_types?.includes("*") ? (
                <span className="text-xs border border-foreground px-2 py-1 rounded-sm">All events</span>
              ) : (sub.event_types || []).map((et) => (
                <span key={et} className="text-[11px] font-mono border border-border px-2 py-1 rounded-sm">{et}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "deliveries" && (
        <div>
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <div className="flex gap-1">
              {["", "succeeded", "failed", "exhausted", "pending"].map((s) => (
                <button
                  key={s || "all"}
                  onClick={() => setStatusFilter(s)}
                  className={`text-[11px] uppercase tracking-widest px-3 py-1.5 border rounded-sm ${statusFilter === s ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"}`}
                  data-testid={`deliveries-filter-${s || "all"}`}
                >{s || "all"}</button>
              ))}
            </div>
            <button onClick={loadDeliveries} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Refresh</button>
          </div>
          {deliveries.length === 0 ? (
            <div className="text-sm text-muted-foreground p-6 border border-dashed border-border rounded-sm text-center" data-testid="deliveries-empty">No deliveries yet for this filter.</div>
          ) : (
            <div className="border border-border rounded-sm overflow-x-auto">
              <table className="w-full text-sm" data-testid="deliveries-table">
                <thead className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left p-3 font-normal">Event</th>
                    <th className="text-left p-3 font-normal">Status</th>
                    <th className="text-left p-3 font-normal">HTTP</th>
                    <th className="text-left p-3 font-normal">Latency</th>
                    <th className="text-left p-3 font-normal">Time</th>
                    <th className="text-right p-3 font-normal"></th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.map((d) => (
                    <tr key={d.id} onClick={() => setDrawerDelivery(d)} className="border-t border-border cursor-pointer hover:bg-muted/20" data-testid={`delivery-row-${d.id}`}>
                      <td className="p-3 font-mono text-xs">
                        {d.status === "succeeded" ? <CheckCircle2 className="w-3 h-3 inline mr-1.5 text-status-ok" strokeWidth={1.5} /> :
                         d.status === "exhausted" ? <XCircle className="w-3 h-3 inline mr-1.5 text-status-tampered" strokeWidth={1.5} /> :
                         <Clock className="w-3 h-3 inline mr-1.5 text-muted-foreground" strokeWidth={1.5} />}
                        {d.event_type}
                      </td>
                      <td className="p-3"><Pill map={DELIVERY_COLOR} value={d.status} /></td>
                      <td className="p-3 text-xs font-mono">{d.response_code ?? "—"}</td>
                      <td className="p-3 text-xs text-muted-foreground">{d.response_time_ms != null ? `${d.response_time_ms}ms` : "—"}</td>
                      <td className="p-3 text-[11px] text-muted-foreground">{d.scheduled_at ? new Date(d.scheduled_at).toLocaleString() : "—"}</td>
                      <td className="p-3 text-right text-[11px] text-muted-foreground">view →</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "rotation" && (
        <div className="border border-border rounded-sm p-5 bg-card max-w-2xl">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Signing secret</div>
          <div className="text-sm space-y-2">
            <div>Current secret prefix: <span className="font-mono">{sub.secret_prefix}…</span></div>
            {sub.rotation ? (
              <div className="text-xs border border-status-info/40 bg-status-info/10 text-status-info p-3 rounded-sm">
                Rotation in progress. Old secret expires <span className="font-mono">{new Date(sub.rotation.expires_at).toLocaleString()}</span>.
                Use the Rotate modal to commit (retire the old) early.
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">No rotation in progress.</div>
            )}
            <button onClick={() => setRotateOpen(true)} className="text-xs border border-foreground bg-foreground text-background px-4 py-2 rounded-sm uppercase tracking-widest mt-2" data-testid="rotation-tab-btn">Rotate now</button>
          </div>
        </div>
      )}

      <DeliveryDrawer delivery={drawerDelivery} onClose={() => setDrawerDelivery(null)} onRetry={retryDelivery} />
      {rotateOpen && <RotateModal open={rotateOpen} sub={sub} onClose={() => setRotateOpen(false)} onComplete={() => load()} />}
    </div>
  );
}
